import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { API_BASE } from "@/lib/utils";

/** A plan as GET /api/v1/plans serves it. */
export interface PlanData {
  tier: string;
  name: string;
  price: number;
  priceLabel: string;
  priceDetail?: string;
  features: string[];
  limits: Record<string, number>;
  credits?: { perMonth: number; overagePrice: number };
}

/**
 * The plan catalogue, and the only place a price should come from.
 *
 * `services/api/src/routes/billing.ts` is the authority. Surfaces that carried
 * their own copy drifted from it: the plan-change modal advertised Growth at
 * "$50/mo" and Scale at "$500/mo" while the API — and the billing page beside
 * it — said "$15 per account, per month" and "$499 per month, plus $10 per
 * account".
 *
 * Restricted to owner/admin at the API, so a caller without that role gets no
 * rows. Render the absence rather than substituting a literal; a wrong price is
 * worse than a missing one.
 */
export function usePlans() {
  return useQuery({
    queryKey: ["plans"],
    queryFn: () => apiFetch<PlanData[]>(`${API_BASE}/api/v1/plans`),
    // The catalogue is a code constant on the server, not per-user state.
    staleTime: 5 * 60 * 1000,
  });
}
